import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import FiscalDocsProviderTypeEnum from '@/api/Enums/FiscalDocsProviderTypeEnum'
import PaymentTypeEnum from '@/api/Enums/PaymentTypeEnum'
import PaymentsCommon from '@/mixins/payments/paymentsCommon'
import {arrayUniqueByObjectKey, clone} from '@/lib/helper/helper.js'
import CompletedData from '@/mixins/tables/completedData.js'
import * as SocketTableHelper from '@/api/SocketTableHelper.js'

export default {
  mixins: [PaymentsCommon, CompletedData],

  props: {
    table: {
      type   : Object,
      default: undefined
    },
    area: {
      type   : Object,
      default: undefined
    },
    visible: {
      type   : Boolean,
      default: false
    }
  },

  data() {
    return {
      selectedItemsTotal         : 0,
      selectedUnpaidItemsTotal   : 0,
      paymentStep                : 1,
      selectedItems              : [],
      selectedReceiptType        : FiscalDocTypeEnum.RECEIPT,
      selectedReceiptData        : null,
      selectedReceiptNotify      : [],
      selectedReceiptNotifyEmails: [],
      selectedUnpaidItems        : [],
      selectedReorderItems       : [],
      totals                     : [],
      loading                    : false,
      viewData                   : null
    }
  },

  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.initData()
      } else {
        this.clearData()
      }
    }
  },

  computed: {
    transactions: {
      get() {
        return this.viewData?.PaymentTransactions || []
      },
      set(val) {
        if (!this.viewData) return
        this.viewData.PaymentTransactions = val
      }
    },

    tableTotals() {
      return this.viewData?.TableTotals || []
    },

    tableTotalsUsed() {
      return this.viewData?.TableTotalsUsed || []
    },

    tableCouponsDiscountTotal() {
      return parseFloat((this.tableCoupons.reduce((value, coupon) => value + coupon.Amount, 0) || 0).toFixed(2))
    },

    tableCouponsUsedDiscountTotal() {
      return parseFloat((this.tableCouponsUsed.reduce((value, coupon) => value + coupon.Amount, 0) || 0).toFixed(2))
    },

    tableCoupons() {
      return this.tableTotals.filter(total => total.Name === 'coupon') || []
    },

    tableCouponsUsed() {
      return this.tableTotalsUsed.filter(total => total.Name === 'coupon') || []
    },

    selectedUnpaidItemsHaveFullDiscount() {
      return !!this.selectedUnpaidItems?.filter(item => item?.Coupon?.Custom === '100.00%' || item?.Coupon?.Custom === '100%').length
    },

    selectedItemsHaveFullDiscount() {
      return !!this.selectedItems?.filter(item => item?.Coupon?.Custom === '100.00%' || item?.Coupon?.Custom === '100%').length
    },

    selectedOrdersHaveItemsWithFullDiscount() {
      let retVal = false
      this.selectedItems.forEach(order => {
        if (order.Items?.filter(item => item?.Coupon?.Custom === '100.00%' || item?.Coupon?.Custom === '100%').length) retVal = true
      })
      return retVal
    },

    tableBalance() {
      let transactionsTotal = parseFloat(this.transactions.reduce((a, b) => a + parseFloat(b.Amount), 0).toFixed(2))

      if (this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) {
        transactionsTotal = parseFloat(this.transactions.filter(transaction => this.eftPosCardTransactionPaidSuccessful(transaction) || PaymentTypeEnum.isMethodWithClearedPayment(transaction.PaymentType)).reduce((a, b) => a + parseFloat(b.Amount), 0).toFixed(2))
      }

      const selectedItemsTotal = parseFloat(this.selectedItemsTotal.toFixed(2))

      return parseFloat((selectedItemsTotal - transactionsTotal - this.tableCouponsDiscountTotal).toFixed(2))
    },

    tableUnpaidBalance() {
      let transactionsTotal = parseFloat(this.transactions.reduce((a, b) => a + parseFloat(b.Amount), 0).toFixed(2))

      if (this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) {
        transactionsTotal = parseFloat(this.transactions.filter(transaction => this.eftPosCardTransactionPaidSuccessful(transaction) || PaymentTypeEnum.isMethodWithClearedPayment(transaction.PaymentType)).reduce((a, b) => a + parseFloat(b.Amount), 0).toFixed(2))
      }
      const selectedItemsTotal = parseFloat(this.selectedUnpaidItemsTotal.toFixed(2))

      return parseFloat((selectedItemsTotal - transactionsTotal - this.tableCouponsDiscountTotal).toFixed(2))
    },

    tableTotalForPayment() {
      const selectedItemsTotal = parseFloat(this.selectedItemsTotal.toFixed(2))

      return selectedItemsTotal - this.tableCouponsDiscountTotal
    },

    tableUnpaidTotalForPayment() {
      const selectedItemsTotal = parseFloat(this.selectedUnpaidItemsTotal.toFixed(2))

      return selectedItemsTotal - this.tableCouponsDiscountTotal
    },

    tableHasTransactions() {
      return !!this.transactions?.length
    },

    tableHasCardPayments() {
      if (this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) {
        return !!this.transactions?.filter(transaction => transaction.PaymentType === PaymentTypeEnum.CARD && this.eftPosCardTransactionPaidSuccessful(transaction))?.length
      }

      return !!this.transactions?.filter(transaction => transaction.PaymentType === PaymentTypeEnum.CARD)?.length
    },

    tableHasCardPaymentsThatRequireReceipt() {
      if (this.appFiscalDocsProviderType === FiscalDocsProviderTypeEnum.CLOUD) {
        return !!this.transactions?.filter(transaction => transaction.PaymentType === PaymentTypeEnum.CARD && this.eftPosCardTransactionPaidSuccessful(transaction))?.length
      }

      return false
    },

    canAddFullTablePayment() {
      return this.loading ||
        this.tableBalance > 0 ||
        this.tableBalance < 0 ||
        (this.hasCashTransaction && !this.cashTransactionAllowed) ||
        (this.appHasFiscalDocsEnabled && this.selectedReceiptType === FiscalDocTypeEnum.INVOICE && !this.selectedReceiptData)
    },

    canAddItemsTablePayment() {
      return this.loading ||
        this.tableUnpaidBalance > 0 ||
        this.tableUnpaidBalance < 0 ||
        (this.hasCashTransaction && !this.cashTransactionAllowed) ||
        (!this.isValidAmountForSelfDeliveryReceipt && FiscalDocTypeEnum.isSelfDoc(this.selectedReceiptType)) ||
        (this.hasNoSelfDeliveryTransaction && FiscalDocTypeEnum.isSelfDoc(this.selectedReceiptType)) ||
        (this.appHasFiscalDocsEnabled && this.selectedReceiptType === FiscalDocTypeEnum.INVOICE && !this.selectedReceiptData)
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },

  methods: {
    initData() {
      this.$bus.$on(window.SocketCommand.Table.Show, this.setData)
      this.$bus.$on(window.SocketCommand.Table.Pay, this.onPayResponse)
      this.$bus.$on(window.SocketCommand.Table.ShowOffline, this.onConnectionRestoreResult)
      this.$bus.$on('hide-app-offline-modal', this.onConnectionRestore)
      this.$bus.$on('add-payment-loader-start', this.startLoader)
      this.$bus.$on('add-payment-loader-stop', this.stopLoader)

      this.stopLoader()
      this.clearData(false)
      this.getData()
    },
    clearData(clearEvents = true) {
      if (clearEvents) {
        this.$bus.$off(window.SocketCommand.Table.Show, this.setData)
        this.$bus.$off(window.SocketCommand.Table.Pay, this.onPayResponse)
        this.$bus.$off(window.SocketCommand.Table.ShowOffline, this.onConnectionRestoreResult)
        this.$bus.$off('hide-app-offline-modal', this.onConnectionRestore)
        this.$bus.$off('add-payment-loader-start', this.startLoader)
        this.$bus.$off('add-payment-loader-stop', this.stopLoader)
      }

      this.viewData = null
      this.selectedItems = []
      this.selectedUnpaidItems = []
      this.selectedReorderItems = []
      this.transactions = []
      this.totals = []
      this.paymentStep = 1
      this.selectedItemsTotal = 0
      this.selectedUnpaidItemsTotal = 0

      this.selectedReceiptType = FiscalDocTypeEnum.RECEIPT
      this.selectedReceiptData = null
      this.selectedReceiptNotify = []
    },

    onConnectionRestoreResult(data, payload) {
      this.setData(data)
      if (!this.viewData?.PaymentTransactions?.length) {
        this.onCancelButtonClick()
        this.$emit('payment-success', data, payload)
      }
    },

    onConnectionRestore() {
      if (this.isVisible) {
        this.startLoader()
        window.callAS(window.SocketCommand.Table.Show, {TableId: this.table.Id}, window.SocketCommand.Table.ShowOffline)
      }
    },

    getData() {
      this.startLoader()
      window.callAS(window.SocketCommand.Table.Show, {TableId: this.table.Id})
    },
    setData(data) {
      this.viewData = data.data || null

      if (this.tableHasTransactions) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.initSelectedUnpaidItems()
          })
        })
      }

      this.stopLoader()
    },

    startLoader() {
      this.$set(this, 'loading', true)
    },
    stopLoader() {
      this.$set(this, 'loading', false)
    },

    getZeroTransaction() {
      return {
        TableId      : this.table.Id,
        CartId       : this.table.CartId,
        TransactionId: Date.now().toString() + (this.transactions.length + 1),
        PaymentType  : PaymentTypeEnum.CASH,
        Received     : 0.00,
        Amount       : 0.00
      }
    },

    onPayResponse(response, payload) {
      if (response.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Restaurant.Tables.Dialog.Payment.PaymentSuccess'),
          type: 'success',
          icon: 'info'
        })
        this.$emit('payment-success', response, payload)
        this.onCancelButtonClick()
      } else {
        const errorMsg = response?.message || this.$t('Common.Error.Generic')
        this.stopLoader()
        this.$bus.$emit('app-show-notification', {
          body   : errorMsg,
          type   : 'error',
          icon   : 'warning',
          timeout: 3000
        })
        SocketTableHelper.getTable(payload?.TableId)
      }
    },

    setSelectedItemsToCart() {
      if (!this.table || !this.selectedUnpaidItems.length) return

      const cartId = this.table?.CartId || null
      const items = clone(this.selectedUnpaidItems || [])

      const payload = {
        CartId: cartId,
        Items : items
      }

      window.callAS(window.SocketCommand.Table.Cart.ItemsForPayment, payload)
    },

    gotoPaymentTransactions() {
      this.setSelectedItemsToCart()
      this.paymentStep = 2
    },

    initSelectedUnpaidItems() {
      const itemsForPayment = this.viewData?.ItemsForPayment
      const items = [...(this.selectedUnpaidItems || []), ...(itemsForPayment || [])]
      this.$set(this, 'selectedUnpaidItems', arrayUniqueByObjectKey(items, 'HashId'))
    },

    onCancelButtonClick() {
      this.isVisible = false
      this.clearData()
    }
  }
}
